/**
 * The global banner: one place where any part of the app shows an app- or
 * device-wide message (unsynced work, session expired, offline limit, low
 * storage, an update). `<GlobalBanner />` renders every visible message in a
 * single container, most important first.
 *
 * React code uses `useBanner` (src/hooks/useBanner.ts), which removes the
 * message when the component unmounts. Anything else calls `showBanner` and
 * must call `hideBanner` on the same state change that made the message
 * obsolete. Messages live in memory only: after a reload each producer posts
 * again from current state, so a stale message cannot survive one.
 *
 * Document-specific messages ("completed elsewhere", finish errors) belong in
 * the document editor, not here.
 */

/** Ordering slots. Pick one; never invent a number. */
export const BannerPriority = {
  sessionExpired: 100,
  offlineLimit: 90,
  lowStorage: 80,
  unsyncedWork: 70,
  update: 50,
  storageProtection: 40,
} as const;

export type BannerSeverity = 'info' | 'warning' | 'critical';

export interface BannerAction {
  label: string;
  /** In-app route (react-router link). */
  to?: string;
  /** External page, opened in a new tab. */
  href?: string;
  onClick?: () => void;
  disabled?: boolean;
}

export interface BannerMessage {
  /** Stable key: showing the same id again replaces the message. */
  id: string;
  priority: (typeof BannerPriority)[keyof typeof BannerPriority];
  severity: BannerSeverity;
  /** One or two plain sentences. */
  text: string;
  /** At most one action per message. */
  action?: BannerAction;
  /** Critical messages should not be dismissible. */
  dismissible?: boolean;
  /**
   * What a dismissal is tied to; defaults to `text`. A dismissed message comes
   * back once this changes (e.g. the count in the text goes up, or a new
   * failure happens).
   */
  dismissKey?: string;
}

type Listener = (messages: readonly BannerMessage[]) => void;

let messages: BannerMessage[] = [];
const dismissed = new Map<string, string>();
let visible: readonly BannerMessage[] = [];
const listeners = new Set<Listener>();

const SEVERITY_RANK: Record<BannerSeverity, number> = { info: 0, warning: 1, critical: 2 };

const dismissKeyOf = (message: BannerMessage) => message.dismissKey ?? message.text;

function sameMessage(a: BannerMessage, b: BannerMessage): boolean {
  return (
    a.priority === b.priority &&
    a.severity === b.severity &&
    a.text === b.text &&
    a.dismissible === b.dismissible &&
    dismissKeyOf(a) === dismissKeyOf(b) &&
    a.action?.label === b.action?.label &&
    a.action?.to === b.action?.to &&
    a.action?.href === b.action?.href &&
    a.action?.disabled === b.action?.disabled &&
    Boolean(a.action?.onClick) === Boolean(b.action?.onClick)
  );
}

function publish(): void {
  visible = messages
    .filter((message) => dismissed.get(message.id) !== dismissKeyOf(message))
    .sort((a, b) => b.priority - a.priority);
  listeners.forEach((listener) => listener(visible));
}

/** Show a message, or replace the one with the same id. */
export function showBanner(message: BannerMessage): void {
  const index = messages.findIndex((existing) => existing.id === message.id);
  if (index >= 0 && sameMessage(messages[index], message)) {
    // Producers re-post on every render; only a fresh onClick closure changed.
    messages[index] = message;
    return;
  }
  messages = index >= 0
    ? messages.map((existing, i) => (i === index ? message : existing))
    : [...messages, message];
  const dismissedKey = dismissed.get(message.id);
  if (dismissedKey !== undefined && dismissedKey !== dismissKeyOf(message)) dismissed.delete(message.id);
  publish();
}

/** Remove a message. A no-op when it is not shown. */
export function hideBanner(id: string): void {
  if (!messages.some((message) => message.id === id)) return;
  messages = messages.filter((message) => message.id !== id);
  dismissed.delete(id);
  publish();
}

/** The user closed a message: hide it until its dismissKey changes. */
export function dismissBanner(id: string): void {
  const message = messages.find((existing) => existing.id === id);
  if (!message?.dismissible) return;
  dismissed.set(id, dismissKeyOf(message));
  publish();
}

/** Visible messages, most important first. Stable reference between changes (safe for useSyncExternalStore). */
export function getBanners(): readonly BannerMessage[] {
  return visible;
}

/** The latest version of a message, including one hidden by a dismissal. */
export function getBanner(id: string): BannerMessage | undefined {
  return messages.find((message) => message.id === id);
}

/** Subscribe to changes. Returns an unsubscribe function. */
export function subscribeBanners(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The most severe level among the messages. */
export function highestSeverity(list: readonly BannerMessage[]): BannerSeverity {
  return list.reduce<BannerSeverity>(
    (highest, message) => (SEVERITY_RANK[message.severity] > SEVERITY_RANK[highest] ? message.severity : highest),
    'info',
  );
}

/** Test-only: drop every message and dismissal. Subscribers are left in place. */
export function resetBannersForTests(): void {
  messages = [];
  dismissed.clear();
  visible = [];
}
